import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { SINGLE_ROUTES } from '~/lib/constants'

import { useGetCustomer } from '@unlikelystudio/react-ecommerce-hooks'

import AddressBook from '~/components/Account/Logged/AddressBook'
import AccountHero from '~/components/Account/Logged/Hero'
import InformationAndChangePassword from '~/components/Account/Logged/InformationAndChangePassword'
import AccountNavigation, {
  AccountNavigationSections,
} from '~/components/Account/Logged/Navigation'
import Orders from '~/components/Account/Logged/Orders'
import Returns from '~/components/Account/Logged/Returns'
import {
  AccountContentProps,
  UrlParamsProps,
} from '~/components/Account/Logged/types'

import { useStyle } from '~/providers/StyleProvider'

import useLocale from '~/hooks/useLocale'

import { getQueryParams } from '~/utils/get-query-params'

import css from './styles.module.scss'

const cx = classnames.bind(css)

function AccountContent({
  className,
  greeting,
  navigation,
  information,
  addressBook,
  orders,
  returns,
}: AccountContentProps) {
  const locale = useLocale()
  const router = useRouter()

  const params = useMemo<UrlParamsProps>(() => {
    return getQueryParams()
  }, [router])

  const [section, setSection] = useState<AccountNavigationSections>(
    (Object.values(AccountNavigationSections).includes(
      params?.section as AccountNavigationSections,
    )
      ? params.section
      : AccountNavigationSections.INFORMATION) as AccountNavigationSections,
  )
  const { data: customer } = useGetCustomer()

  const gridStyle = useStyle({
    grid: GlobalGridPreset.BASE_GRID,
  })

  const addOrderNumberParam =
    params?.number && section === AccountNavigationSections.ORDERS

  const updateCurrentPathname = () => {
    router.push(
      SINGLE_ROUTES.account.href,
      `${SINGLE_ROUTES.account.href}?section=${section}${
        addOrderNumberParam ? `&number=${params.number}` : ''
      }`,
      {
        shallow: true,
        locale,
      },
    )
  }

  const renderSection = (currentSection?: AccountNavigationSections) => {
    switch (currentSection) {
      case AccountNavigationSections.ADDRESS_BOOK:
        return (
          <AddressBook className={cx(css.content, css.full)} {...addressBook} />
        )
      case AccountNavigationSections.ORDERS:
        return <Orders className={cx(css.content, css.full)} {...orders} />
      case AccountNavigationSections.RETURNS:
        return <Returns className={cx(css.content, css.full)} {...returns} />
      case AccountNavigationSections.INFORMATION:
      default:
        return (
          <InformationAndChangePassword
            className={css.content}
            customer={customer}
            information={information}
          />
        )
    }
  }

  useEffect(() => {
    updateCurrentPathname()
  }, [section])

  return (
    <>
      <AccountHero greeting={greeting} name={customer?.firstName} />
      <section className={cx(className, css.AccountContent, gridStyle)}>
        <AccountNavigation
          className={css.navigation}
          activeSection={section}
          setSection={setSection}
          {...navigation}
        />

        {renderSection(section)}
      </section>
    </>
  )
}

export default AccountContent
